import { CommonRegistry } from '@common/components/components';
import { CommonServiceContainer } from '@common/services/CommonServiceContainer';
import { EquipmentService } from '@common/services/EquipmentService';
import { InventoryService } from '@common/services/InventoryService';
import { World } from '@common/World';

import { AssetManager } from '../AssetManager';
import { CameraService } from './CameraService';
import { ClientChunkDataService } from './ClientChunkDataService';
import { ClientInventoryService } from './ClientInventoryService';
import { ClientMessageDispatchService } from './ClientMessageDispatchService';
import { ClientNetworkEventHandler } from './ClientNetworkEventHandler';
import { ClientWorldService } from './ClientWorldService';
import { ConnectionService } from './ConnectionService';
import { ConnectionStateService } from './ConnectionStateService';
import { DebugService } from './DebugService';
import { ClientDialogueService } from './DialogueService';
import { EmoticonService } from './EmoticonService';
import { EntityInspectorService } from './EntityInspectorService';
import { FootstepParticleService } from './FootstepParticleService';
import { InputBlockingService } from './InputBlockingService';
import { InventoryUIService } from './InventoryUIService';
import { LightingService } from './LightingService';
import { LocalPlayerService } from './LocalPlayerService';
import { PixiInstanceService } from './PixiInstanceService';
import { RenderService } from './RenderService';
import { RepresentationService } from './RepresentationService';
import { SpatialSoundService } from './SpatialSoundService';
import { SpritesheetService } from './SpritesheetService';

/**
 * Service container for client-specific services.
 * Composes with CommonServiceContainer to access common services.
 */
export class ClientServiceContainer {
  public readonly common: CommonServiceContainer;
  public readonly camera: CameraService;
  public readonly chunkData: ClientChunkDataService;
  public readonly clientWorld: ClientWorldService;
  public readonly connection: ConnectionService;
  public readonly connectionState: ConnectionStateService;
  public readonly debug: DebugService;
  public readonly lighting: LightingService;
  public readonly localPlayer: LocalPlayerService;
  public readonly render: RenderService;
  public readonly spritesheet: SpritesheetService;
  public readonly networkEventHandler: ClientNetworkEventHandler;
  public readonly messageDispatch: ClientMessageDispatchService;
  public readonly dialogue: ClientDialogueService;
  public readonly inputBlocking: InputBlockingService;
  public readonly inventoryUI: InventoryUIService;
  public readonly entityInspector: EntityInspectorService;
  public readonly clientInventory: ClientInventoryService;
  public readonly emoticon: EmoticonService;
  public readonly representation: RepresentationService;
  public readonly footstepParticle: FootstepParticleService;
  public readonly spatialSound: SpatialSoundService;

  public readonly inventory: InventoryService;
  public readonly equipment: EquipmentService;
  public readonly pixiInstance: PixiInstanceService;

  constructor(common: CommonServiceContainer, clientWorldService: ClientWorldService) {
    this.common = common;
    this.clientWorld = clientWorldService;
    this.camera = new CameraService();
    this.chunkData = new ClientChunkDataService();
    this.connection = new ConnectionService();
    this.connectionState = new ConnectionStateService();
    this.debug = new DebugService();
    this.lighting = new LightingService();
    this.localPlayer = new LocalPlayerService();
    this.spatialSound = new SpatialSoundService();
    this.render = new RenderService();
    this.spritesheet = new SpritesheetService(AssetManager.getInstance());
    this.messageDispatch = new ClientMessageDispatchService(common);
    this.inputBlocking = new InputBlockingService();
    this.dialogue = new ClientDialogueService(this.clientWorld);
    this.dialogue.setInputBlocking(this.inputBlocking);
    this.inventoryUI = new InventoryUIService(this.localPlayer, this.clientWorld);
    this.inventoryUI.setInputBlocking(this.inputBlocking);
    this.entityInspector = new EntityInspectorService();

    // Client-side inventory service for entity ID translation and deferred resolution
    this.clientInventory = new ClientInventoryService(this.clientWorld);
    this.clientInventory.setInventoryUIService(this.inventoryUI);
    this.clientWorld.registerTranslator(
      'Inventory',
      this.clientInventory.translateInventoryData.bind(this.clientInventory),
    );
    this.clientWorld.registerTranslator(
      'Equipment',
      this.clientInventory.translateEquipmentData.bind(this.clientInventory),
    );
    this.clientWorld.registerEntityCreationListener(
      this.clientInventory.onEntityCreated.bind(this.clientInventory),
    );
    this.clientWorld.registerWorldResetListener(
      this.clientInventory.onWorldReset.bind(this.clientInventory),
    );

    // Client-side inventory service for optimistic ECS mutations
    const clientWorld = clientWorldService.getWorld() as unknown as World<CommonRegistry>;
    this.inventory = new InventoryService(clientWorld);
    // Equipment service for equip/unequip validation and operations
    this.equipment = new EquipmentService(clientWorld, this.inventory, common.eventBus);
    // Wire up inventory and equipment services to UI service for ECS-level optimistic mutations
    this.inventoryUI.setInventoryService(this.inventory);
    this.inventoryUI.setEquipmentService(this.equipment);
    this.pixiInstance = new PixiInstanceService();
    this.representation = new RepresentationService(this.spritesheet);
    this.emoticon = new EmoticonService(this);
    this.footstepParticle = new FootstepParticleService(this.spritesheet);

    this.networkEventHandler = new ClientNetworkEventHandler(this);
    this.networkEventHandler.registerHandlers();
  }
}
